import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SortingState } from "@tanstack/react-table";
import {
    Search,
    LayoutGrid,
    Table as TableIcon,
} from "@/components/icons";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuSeparator,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useUnitsData } from "@/hooks/useUnitsData";
import { useStats } from "@/hooks/useStats";
import { useAuth } from "@/app/AuthContext";
import { useColumnConfig } from "@/hooks/useColumnConfig";
import { ChargerTable } from "@/components/ChargerTable";
import { ChargerVitrine } from "@/components/ChargerVitrine";
import { ChargerDetailPanel } from "@/views/ChargerDetailPanel";
import { SlidePanel } from "@/components/SlidePanel";
import { ColumnChooser } from "@/components/ColumnChooser";
import type { Unit } from "@/lib/api";

export interface UnitsExplorerFilters {
    search: string;
    stateFilter: string[];
    sortBy: string;
    viewMode: "table" | "vitrine";
    // Charger type: "residential" | "commercial" (work_mode). Empty = all types.
    typeFilter: string[];
    // Pricing: "paid" | "free" (pricing_mode), only meaningful for commercial
    // chargers. Empty = any pricing.
    pricingFilter: string[];
    // Protocol versions ("0", "9", …) — admin-only control. Empty = all.
    protocolFilter: string[];
}

export interface UnitsExplorerProps {
    filters: UnitsExplorerFilters;
    onFiltersChange: (filters: UnitsExplorerFilters) => void;
    // When the caller is a residential driver, the "no units" empty state offers
    // a self-claim CTA instead of the "contact your administrator" dead-end.
    canClaim?: boolean;
    onClaim?: () => void;
}

export function sanitizeTypePricing(
    typeFilter: string[],
    pricingFilter: string[],
): { typeFilter: string[]; pricingFilter: string[] } {
    if (!typeFilter.includes("commercial")) {
        return { typeFilter, pricingFilter: [] };
    }
    return { typeFilter, pricingFilter };
}

const PAGE_SIZE = 100;

const COLUMN_DEFS = [
    { id: "status", label: "Status" },
    { id: "name", label: "Unit" },
    { id: "unit_id_col", label: "Unit ID" },
    { id: "product", label: "Product" },
    { id: "voltage", label: "Voltage" },
    { id: "current", label: "Current" },
    { id: "session_energy", label: "Session Energy" },
    { id: "power_factor", label: "Power Factor" },
    { id: "protocol_version", label: "Protocol" },
    { id: "location", label: "Location" },
];

const STATUS_OPTIONS = [
    { value: "charging", label: "Charging" },
    { value: "standby", label: "Standby" },
    { value: "ready", label: "Ready" },
    { value: "connected", label: "Connected" },
    { value: "blocked", label: "Blocked" },
    { value: "error", label: "Error" },
    { value: "offline", label: "Offline" },
    { value: "unsupported", label: "Unsupported Protocol" },
] as const;

const TYPE_OPTIONS = [
    { value: "residential", label: "Residential" },
    { value: "commercial", label: "Commercial" },
] as const;

const PRICING_OPTIONS = [
    { value: "paid", label: "Paid" },
    { value: "free", label: "Free" },
] as const;

function sortingToParam(sorting: SortingState, fallback: string): string {
    if (!sorting.length) return fallback;
    const s = sorting[0];
    const map: Record<string, string> = {
        name: "name",
        status: "state",
        voltage: "voltage",
        current: "current",
        session_energy: "power",
        unit_id_col: "unit_id",
    };
    return map[s.id] || fallback;
}

export function UnitsExplorer({ filters, onFiltersChange, canClaim = false, onClaim }: UnitsExplorerProps) {
    const { search, stateFilter, sortBy, viewMode, typeFilter, pricingFilter, protocolFilter } = filters;
    const stateParam = stateFilter.join(",");
    const modeParam = typeFilter.join(",");
    const pricingParam = pricingFilter.join(",");
    const protocolParam = protocolFilter.join(",");
    // Protocol filter is an admin diagnostic; options come from the fleet
    // stats snapshot (protocol_counts — {} for org-scoped users).
    const { isAdmin } = useAuth();
    const { protocolCounts } = useStats();
    const protocolOptions = Object.entries(protocolCounts)
        .sort((a, b) => Number(b[1]) - Number(a[1]));

    const update = useCallback(
        (patch: Partial<UnitsExplorerFilters>) =>
            onFiltersChange({ ...filters, ...patch }),
        [filters, onFiltersChange],
    );

    const commercialSelected = typeFilter.includes("commercial");
    // Pricing is a commercial-only concept; show it once Commercial is picked,
    // or while a pricing value is still active (so the user can clear it).
    const showPricing = commercialSelected || pricingFilter.length > 0;

    const toggleType = useCallback(
        (value: string, active: boolean) => {
            const nextTypes = active
                ? typeFilter.filter((t) => t !== value)
                : [...typeFilter, value];
            // Whenever Commercial ends up unselected, drop any pricing filter so
            // a now-hidden pricing value can't keep silently narrowing results.
            update(sanitizeTypePricing(nextTypes, pricingFilter));
        },
        [typeFilter, pricingFilter, update],
    );

    const togglePricing = useCallback(
        (value: string, active: boolean) => {
            update({
                pricingFilter: active
                    ? pricingFilter.filter((p) => p !== value)
                    : [...pricingFilter, value],
            });
        },
        [pricingFilter, update],
    );

    // TanStack sorting state (internal)
    const [sorting, setSorting] = useState<SortingState>([]);

    // Sync TanStack sorting → parent sortBy
    useEffect(() => {
        const param = sortingToParam(sorting, sortBy);
        if (param !== sortBy) update({ sortBy: param });
    }, [sorting]);

    // Paging
    const [pageEnd, setPageEnd] = useState(PAGE_SIZE);
    useEffect(() => setPageEnd(PAGE_SIZE), [search, stateParam, sortBy, modeParam, pricingParam, protocolParam]);

    // Data — join arrays into comma-separated strings for the API
    const { units, total, noAccess, degraded, truncated, pageLimit, isLoading } = useUnitsData({
        search: search.trim(),
        state: stateParam,
        sort: sortBy,
        start: 0,
        end: pageEnd,
        mode: modeParam,
        pricing: pricingParam,
        protocol: protocolParam,
    });

    // A truncated page is the server's page limit: asking for a wider window
    // returns the same rows, so stop offering more and ask to narrow instead.
    const hasMore = units.length < total && !truncated;
    const loadMore = useCallback(() => setPageEnd((p) => p + PAGE_SIZE), []);

    // Column config
    const { columnVisibility, setColumnVisibility, resetToDefaults } = useColumnConfig();

    // Detail panel
    const [selectedUnitId, setSelectedUnitId] = useState<string | null>(
        () => localStorage.getItem("dashboardSelectedUnit")
    );
    const handleUnitClick = useCallback((unit: Unit) => {
        setSelectedUnitId(unit.unit_id);
        localStorage.setItem("dashboardSelectedUnit", unit.unit_id);
    }, []);
    // Double-click opens the full charger details page (single click = panel).
    const navigate = useNavigate();
    const handleUnitOpen = useCallback(
        (unit: Unit) => navigate(`/unit/${unit.unit_id}`),
        [navigate],
    );
    const closePanel = useCallback(() => {
        setSelectedUnitId(null);
        localStorage.removeItem("dashboardSelectedUnit");
    }, []);

    const handleColumnToggle = useCallback(
        (colId: string, visible: boolean) => {
            setColumnVisibility((prev) => ({ ...prev, [colId]: visible }));
        },
        [setColumnVisibility],
    );

    const statusSet = new Set(stateFilter);
    const statusCount = stateFilter.length;
    const typeSet = new Set(typeFilter);
    const typeCount = typeFilter.length;
    const pricingSet = new Set(pricingFilter);
    const pricingCount = pricingFilter.length;

    return (
        <>
            {/* Controls */}
            <Card>
                <CardContent className="gap-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Search */}
                        <div className="flex flex-1 min-w-[240px]">
                            <Button variant="ghost" size="sm" aria-label="Search">
                                <Search className="h-4 w-4"/>
                            </Button>
                            <input
                                className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                placeholder="Search Unit ID, Name or IP (use ^prefix for starts-with)..."
                                value={search}
                                onChange={(e) => update({ search: e.target.value })}
                            />
                        </div>

                        {/* State filter (multi-select) */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-1.5">
                                    {statusCount === 0
                                        ? "All statuses"
                                        : statusCount === 1
                                            ? STATUS_OPTIONS.find((o) => o.value === stateFilter[0])?.label ?? stateFilter[0]
                                            : "Statuses"}
                                    {statusCount > 1 && (
                                        <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                                            {statusCount}
                                        </Badge>
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-52">
                                {STATUS_OPTIONS.map((opt) => {
                                    const active = statusSet.has(opt.value);
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={opt.value}
                                            checked={active}
                                            onSelect={(e) => {
                                                e.preventDefault();
                                                const next = active
                                                    ? stateFilter.filter((s) => s !== opt.value)
                                                    : [...stateFilter, opt.value];
                                                update({ stateFilter: next });
                                            }}
                                        >
                                            {opt.label}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })}
                                {statusCount > 0 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onSelect={() => update({ stateFilter: [] })}
                                            className="text-xs opacity-70"
                                        >
                                            Clear filters
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Type filter (residential / commercial, multi-select) */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-1.5">
                                    {typeCount === 0
                                        ? "All types"
                                        : typeCount === 1
                                            ? TYPE_OPTIONS.find((o) => o.value === typeFilter[0])?.label ?? typeFilter[0]
                                            : "Types"}
                                    {typeCount > 1 && (
                                        <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                                            {typeCount}
                                        </Badge>
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-48">
                                {TYPE_OPTIONS.map((opt) => {
                                    const active = typeSet.has(opt.value);
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={opt.value}
                                            checked={active}
                                            onSelect={(e) => {
                                                e.preventDefault();
                                                toggleType(opt.value, active);
                                            }}
                                        >
                                            {opt.label}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })}
                                {typeCount > 0 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onSelect={() => update({ typeFilter: [], pricingFilter: [] })}
                                            className="text-xs opacity-70"
                                        >
                                            Clear filters
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Pricing filter (paid / free) — commercial chargers only */}
                        {showPricing && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="gap-1.5">
                                        {pricingCount === 0
                                            ? "All pricing"
                                            : pricingCount === 1
                                                ? PRICING_OPTIONS.find((o) => o.value === pricingFilter[0])?.label ?? pricingFilter[0]
                                                : "Pricing"}
                                        {pricingCount > 1 && (
                                            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                                                {pricingCount}
                                            </Badge>
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-40">
                                    {PRICING_OPTIONS.map((opt) => {
                                        const active = pricingSet.has(opt.value);
                                        return (
                                            <DropdownMenuCheckboxItem
                                                key={opt.value}
                                                checked={active}
                                                onSelect={(e) => {
                                                    e.preventDefault();
                                                    togglePricing(opt.value, active);
                                                }}
                                            >
                                                {opt.label}
                                            </DropdownMenuCheckboxItem>
                                        );
                                    })}
                                    {pricingCount > 0 && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                onSelect={() => update({ pricingFilter: [] })}
                                                className="text-xs opacity-70"
                                            >
                                                Clear filters
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Protocol version filter — admin diagnostic. Options
                            come from the fleet stats snapshot with live counts;
                            filtering happens server-side (?protocol=). */}
                        {isAdmin && protocolOptions.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="gap-1.5">
                                        {protocolFilter.length === 0
                                            ? "All protocols"
                                            : protocolFilter.length === 1
                                                ? `Proto v${protocolFilter[0]}`
                                                : "Protocols"}
                                        {protocolFilter.length > 1 && (
                                            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                                                {protocolFilter.length}
                                            </Badge>
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-44">
                                    {protocolOptions.map(([ver, count]) => {
                                        const active = protocolFilter.includes(ver);
                                        return (
                                            <DropdownMenuCheckboxItem
                                                key={ver}
                                                checked={active}
                                                onSelect={(e) => {
                                                    e.preventDefault();
                                                    update({
                                                        protocolFilter: active
                                                            ? protocolFilter.filter((p) => p !== ver)
                                                            : [...protocolFilter, ver],
                                                    });
                                                }}
                                            >
                                                v{ver} ({Number(count).toLocaleString()})
                                            </DropdownMenuCheckboxItem>
                                        );
                                    })}
                                    {protocolFilter.length > 0 && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                onSelect={() => update({ protocolFilter: [] })}
                                                className="text-xs opacity-70"
                                            >
                                                Clear filters
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Sort */}
                        <Select value={sortBy} onValueChange={(v) => {
                            update({ sortBy: v });
                            setSorting([]);
                        }}>
                            <SelectTrigger size="sm">
                                <SelectValue placeholder="Sort by Name"/>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="name">Sort by Name</SelectItem>
                                <SelectItem value="unit_id">Sort by Unit ID</SelectItem>
                                <SelectItem value="state">Sort by State</SelectItem>
                                <SelectItem value="current">Sort by Current</SelectItem>
                                <SelectItem value="power">Sort by Power</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* View toggle */}
                        <ToggleGroup type="single" size="sm" variant="outline" value={viewMode}
                                     onValueChange={(v) => v && update({ viewMode: v as "table" | "vitrine" })}>
                            <ToggleGroupItem value="table" aria-label="Table View">
                                <TableIcon className="h-4 w-4"/>
                            </ToggleGroupItem>
                            <ToggleGroupItem value="vitrine" aria-label="Card View">
                                <LayoutGrid className="h-4 w-4"/>
                            </ToggleGroupItem>
                        </ToggleGroup>

                        {/* Column chooser (table only) */}
                        {viewMode === "table" && (
                            <ColumnChooser
                                columns={COLUMN_DEFS}
                                visibility={columnVisibility}
                                onToggle={handleColumnToggle}
                                onReset={resetToDefaults}
                            />
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Units Content + Detail Panel */}
            <div className="flex gap-4 items-stretch">
                {/* Main content — shrinks when panel is open */}
                <div className="flex-1 min-w-0">
                    {degraded && (
                        <div role="alert" className="mb-3 rounded-md bg-amber-400/15 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                            The charger list is temporarily unavailable — the list index is not being
                            updated. This is not an empty fleet; it will reload automatically.
                        </div>
                    )}
                    {truncated && !degraded && (
                        <div role="status" className="mb-3 rounded-md bg-muted px-3 py-2 text-sm opacity-80">
                            Showing the first {(pageLimit ?? units.length).toLocaleString()} of{" "}
                            {total.toLocaleString()} chargers. Narrow the search or filters to see the rest.
                        </div>
                    )}
                    {noAccess ? (
                        <Card>
                            <CardContent className="items-center text-center">
                                {canClaim ? (
                                    <>
                                        <div className="text-4xl">🔌</div>
                                        <div className="text-lg font-semibold">No chargers yet</div>
                                        <div className="opacity-70">
                                            Claim the charger installed at your home to see live
                                            status, set schedules, and control charging.
                                        </div>
                                        <Button className="mt-3" onClick={onClaim}>
                                            Claim a charger
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-4xl">🔒</div>
                                        <div className="text-lg font-semibold">No Units Assigned</div>
                                        <div className="opacity-70">
                                            Contact your administrator to get access to charging units.
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    ) : viewMode === "table" ? (
                        <ChargerTable
                            units={units}
                            total={total}
                            hasMore={hasMore}
                            isLoading={isLoading}
                            onLoadMore={loadMore}
                            onRowClick={handleUnitClick}
                            onRowDoubleClick={handleUnitOpen}
                            selectedUnitId={selectedUnitId ?? undefined}
                            columnVisibility={columnVisibility}
                            onColumnVisibilityChange={setColumnVisibility}
                            sorting={sorting}
                            onSortingChange={setSorting}
                        />
                    ) : (
                        <ChargerVitrine
                            units={units}
                            total={total}
                            isLoading={isLoading}
                            onUnitClick={handleUnitClick}
                            onUnitDoubleClick={handleUnitOpen}
                            selectedUnitId={selectedUnitId ?? undefined}
                        />
                    )}
                </div>

                {/* Detail panel — inline, pushes content left */}
                <SlidePanel
                    open={!!selectedUnitId}
                    onClose={closePanel}
                    title="Charger Details"
                >
                    {selectedUnitId && <ChargerDetailPanel unitId={selectedUnitId}/>}
                </SlidePanel>
            </div>
        </>
    );
}
